

// cspell:disable

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import blogFetch from '../../axios/config';
import toast from 'react-hot-toast';
import { ModalApontamentoEmbolsadoProps } from './Types';


const ModalApontamentoEmbolsado: React.FC<ModalApontamentoEmbolsadoProps> = ({ show, onHide }) => {

    const [peca, setPeca] = useState({
        ordem: "",
        tipoPeca: "",
        codigoVeiculo: "",
        projeto: "",
        veiculo: "",
        ultimoApont: "",
        prensadora: ""
    });


    const inputRef = useRef<HTMLInputElement>(null);
    const [matricula, setMatricula] = useState<string>("");
    const [matriculaSAP, setMatriculaSAP] = useState<string>("");
    const [nomeUsuario, setNomeUsuario] = useState<string>("");
    const [validandoMatricula, setValidandoMatricula] = useState<boolean>(false);

    // Valida a matrícula SAP contra o backend. Retorna { nome, matriculaSAP } se existir, senão null.
    const validarMatriculaSAP = useCallback(async (matricula: string): Promise<{ nome: string; matriculaSAP: string } | null> => {
        const valor = matricula.trim();
        if (!/^\d{6}$/.test(valor)) return null;
        try {
            const { data } = await blogFetch.get(
                `IAcompanhamentoMontagem/validate-matricula/${encodeURIComponent(valor)}`
            );
            if (!data?.exists) return null;
            return {
                nome: data.nome ?? "",
                matriculaSAP: data.matricula_SAP ?? "",
            };
        } catch (err) {
            // 404 (exists:false) cai aqui via axios; tratamos como inválida
            return null;
        }
    }, []);


    const handleSubmit = async () => {
        try {
            if (!peca.ordem.trim()) {
                toast.error("Por favor, insira a ordem da peça.");
                return;
            }

            if (!/^\d{6}$/.test(matricula) || !matriculaSAP) {
                toast.error("Registre uma matrícula SAP válida.");
                return;
            }

            if (!peca.prensadora.trim()) {
                toast.error("Por favor, selecione o prensador.");
                return;
            }

            const payload = {
                Ordem: peca.ordem,
                Matricula: matriculaSAP,
                prensadora: peca.prensadora
            };

            await blogFetch.post(`ISAGA_ApontamentoEmbolsado/apontamento_sap`, payload);

            toast.success(`Apontamento SAP realizado para a ordem: ${peca.ordem}`);
            resetForm();
        } catch (error: any) {
            console.error("Erro ao realizar apontamento:", error);
            toast.error(error.response?.data?.message || "Erro ao realizar apontamento SAP.");
        }
    };

    // Aceita apenas números, máximo 6 dígitos; limpa a validação anterior ao editar
    const handleMatriculaChange = (valor: string) => {
        setMatricula(valor.replace(/\D/g, "").slice(0, 6));
        setMatriculaSAP("");
        setNomeUsuario("");
    };

    const resetForm = () => {
        setPeca({
            ordem: "",
            tipoPeca: "",
            codigoVeiculo: "",
            projeto: "",
            veiculo: "",
            ultimoApont: "",
            prensadora: ""
        });
    };


    const handleCancel = () => {
        resetForm();
        onHide();
    };

    // Ao abrir o modal, prefill da matrícula salva (compartilhada com o 14MONTAG) e re-valida
    useEffect(() => {
        if (!show) return;
        const matriculaStorage = localStorage.getItem("listaMontagem_matriculas_SAP");
        if (!matriculaStorage) return;
        let valor = "";
        let nomeSalvo = "";
        let matriculaSAPSalva = "";
        try {
            const obj = JSON.parse(matriculaStorage);
            valor = obj.principal || obj.secundaria || "";
            nomeSalvo = obj.nome || "";
            matriculaSAPSalva = obj.matriculaSAP || "";
        } catch {
            valor = matriculaStorage;
        }
        if (!valor) return;

        setMatricula(valor);
        setNomeUsuario(nomeSalvo); // mostra o nome salvo enquanto re-valida
        setMatriculaSAP(matriculaSAPSalva);

        (async () => {
            const resultado = await validarMatriculaSAP(valor);
            if (resultado === null) {
                localStorage.removeItem("listaMontagem_matriculas_SAP");
                setMatricula("");
                setNomeUsuario("");
                setMatriculaSAP("");
                toast.error("Matrícula não encontrada no SAP");
            } else {
                setNomeUsuario(resultado.nome);
                setMatriculaSAP(resultado.matriculaSAP);
                localStorage.setItem(
                    "listaMontagem_matriculas_SAP",
                    JSON.stringify({ principal: valor, nome: resultado.nome, matriculaSAP: resultado.matriculaSAP })
                );
            }
        })();
    }, [show, validarMatriculaSAP]);

    // Valida automaticamente (debounce) assim que a matrícula chega a 6 dígitos
    useEffect(() => {
        if (!/^\d{6}$/.test(matricula) || matriculaSAP) return;
        const timeout = setTimeout(async () => {
            setValidandoMatricula(true);
            const resultado = await validarMatriculaSAP(matricula);
            setValidandoMatricula(false);
            if (resultado === null) {
                setNomeUsuario("");
                setMatriculaSAP("");
                toast.error("Matrícula não encontrada no SAP");
                return;
            }
            setNomeUsuario(resultado.nome);
            setMatriculaSAP(resultado.matriculaSAP);
            localStorage.setItem(
                "listaMontagem_matriculas_SAP",
                JSON.stringify({ principal: matricula, nome: resultado.nome, matriculaSAP: resultado.matriculaSAP })
            );
            toast.success(`Matrícula registrada: ${resultado.nome}`);
        }, 600);
        return () => clearTimeout(timeout);
    }, [matricula, matriculaSAP, validarMatriculaSAP]);

    useEffect(() => {
        if (!show || !peca.ordem?.trim()) return;
        const timeout = setTimeout(() => buscarPeca(peca.ordem), 800);
        return () => clearTimeout(timeout);
    }, [peca.ordem, show]);


    const buscarPeca = async (ordem: string) => {
        try {

            const { data } = await blogFetch.get(`ISAGA_ApontamentoEmbolsado/getPeca/${ordem}`);



            if (!data) {
                toast.error("Peça não encontrada.");
                return;
            }
            setPeca((prev) => ({
                ...prev,
                codigoVeiculo: data.ordem_VehCod || "",
                projeto: data.subProduto_Desc || "",
                tipoPeca: data.ordem_Part_Short || "",
                veiculo: data.ordem_VehDesc || "",
                ultimoApont: data.ordem_UltSetApontado || "",
            }));
        } catch (error: any) {
            console.error("Erro ao buscar peça:", error);
            toast.error(error.response?.data || "Erro ao buscar peça.");
            resetForm();
        }
    };


    return (
        <Modal show={show} onHide={handleCancel} centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Apontamento Embolsado SAP</Modal.Title>
            </Modal.Header>
            <Modal.Body>

                <div className="card p-3 shadow-sm rounded-3 mb-3">
                    <Form onSubmit={(e: any) => e.preventDefault()}>
                        <Form.Group className="mb-3">
                            <Form.Label>Ordem</Form.Label>
                            <Form.Control
                                type="text"
                                value={peca.ordem}
                                ref={inputRef}
                                onChange={(e) => setPeca((p) => ({ ...p, ordem: e.target.value }))}
                                placeholder="Digite a ordem da peça"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Matrícula SAP</Form.Label>
                            <Form.Control
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                value={matricula}
                                onChange={(e) => handleMatriculaChange(e.target.value)}
                                placeholder="Digite a matrícula SAP (6 dígitos)"
                                isInvalid={matricula.length === 6 && !validandoMatricula && !matriculaSAP}
                                isValid={!!matriculaSAP}
                            />
                            <Form.Control.Feedback type="invalid">
                                Matrícula não encontrada no SAP.
                            </Form.Control.Feedback>
                            {validandoMatricula ? (
                                <small className="text-muted d-block mt-1">Validando matrícula...</small>
                            ) : nomeUsuario ? (
                                <small className="text-success fw-semibold d-block mt-1">
                                    Operador: {nomeUsuario}
                                </small>
                            ) : null}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Seladora</Form.Label>
                            <Form.Control
                                as="select"
                                value={peca.prensadora}
                                onChange={(e) => setPeca((p) => ({ ...p, prensadora: e.target.value }))}
                                placeholder="Escolha o prensador"
                            >
                                <option value="">Selecione a Seladora</option>
                                <option value="PR01">SL-01</option>
                                <option value="PR02">SL-02</option>
                                <option value="PR03">SL-03</option>
                                <option value="PR04">Sl-04</option>
                            </Form.Control>
                        </Form.Group>
                    </Form>

                    <div className="card p-3 shadow-sm rounded-3">
                        <h6 className="fw-bold text-secondary mb-3">Informações do Veículo</h6>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label small fw-semibold text-muted">Tipo Peça</label>
                                <input value={peca.tipoPeca} className="form-control" readOnly />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small fw-semibold text-muted">Código Veículo</label>
                                <input value={peca.codigoVeiculo} className="form-control" readOnly />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small fw-semibold text-muted">Ultimo Apontamento</label>
                                <input value={peca.ultimoApont} className="form-control" readOnly />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small fw-semibold text-muted">Projeto</label>
                                <input value={peca.projeto} className="form-control" readOnly />
                            </div>
                            <div className="col-12">
                                <label className="form-label small fw-semibold text-muted">Veículo</label>
                                <input value={peca.veiculo} className="form-control" readOnly />
                            </div>


                        </div>
                    </div>

                </div>

            </Modal.Body>
            <Modal.Footer className="d-flex justify-evenly">
                <button className="btn btn-secondary justify-end" onClick={handleCancel}>Cancelar</button>
                <button
                    className="btn btn-primary justify-start"
                    onClick={() => { handleSubmit() }}
                    disabled={!matriculaSAP || validandoMatricula || !peca.prensadora.trim()}
                    title={
                        !matriculaSAP
                            ? "Valide a matrícula SAP para apontar"
                            : !peca.prensadora.trim()
                                ? "Selecione a seladora para apontar"
                                : undefined
                    }
                >Apontar sap</button>
            </Modal.Footer>

        </Modal >
    );
};

export default ModalApontamentoEmbolsado;